import { useSourcingRun, useUpdateJobStatus, useUsers } from "@vidario/api-client";
import { closeJobPlan, type Job } from "@vidario/domain";
import { Button, ConfirmDialog, Modal, useToast } from "@vidario/ui";
import { type ReactElement } from "react";

import { useAuth } from "../../providers/auth-provider";

export interface CloseJobDialogProps {
  job: Pick<Job, "id" | "title" | "createdBy">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** "Stelle schließen" with a confirmation (#19). Closing can't be undone and
 * freezes the candidates, so a stray click must not be enough. While a
 * candidate search is running the job can't be closed at all. */
export function CloseJobDialog({ job, open, onOpenChange }: CloseJobDialogProps): ReactElement {
  const toast = useToast();
  const { user } = useAuth();
  const runQuery = useSourcingRun(job.id, { enabled: open });
  const { data: users = [] } = useUsers();
  const closeJob = useUpdateJobStatus();

  const plan = closeJobPlan({
    ownerId: job.createdBy,
    actorId: user?.id ?? "",
    // Unknown until the query settles; if it fails, the backend still refuses
    // a close during a search, so don't keep the dialog waiting forever.
    latestRunStatus: runQuery.isSuccess
      ? (runQuery.data?.status ?? null)
      : runQuery.isError
        ? null
        : undefined,
  });
  const owner = users.find((u) => u.id === job.createdBy);

  const handleConfirm = (): void => {
    closeJob.mutate(
      { id: job.id, status: "closed" },
      {
        onSuccess: (updated) => {
          onOpenChange(false);
          toast.push({
            kind: "success",
            title: "Stelle geschlossen",
            message: `„${updated.title}“ wurde geschlossen.`,
          });
        },
        onError: (err) => toast.push({ kind: "error", title: "Fehler", message: err.message }),
      },
    );
  };

  if (plan.blocked) {
    return (
      <Modal
        open={open}
        onOpenChange={onOpenChange}
        title="Stelle kann gerade nicht geschlossen werden"
        size="sm"
        footer={<Button onClick={() => onOpenChange(false)}>OK</Button>}
      >
        <p className="text-sm text-text-primary">
          Die Kandidatensuche läuft noch. Warte, bis sie fertig ist.
        </p>
      </Modal>
    );
  }

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Stelle schließen?"
      confirmLabel="Stelle schließen"
      // A reflex Enter keeps everything (#186).
      initialFocus="cancel"
      tone="destructive"
      loading={closeJob.isPending || plan.checking}
      onConfirm={handleConfirm}
    >
      <p>
        „{job.title}“ wird geschlossen. Danach kommen keine neuen Kandidaten hinzu, und
        Kandidaten-Status lassen sich nicht mehr ändern. Die Stelle kann nicht wieder geöffnet
        werden.
      </p>
      {plan.notifiesOwner && (
        <p className="mt-3">
          {owner ? (
            <>
              <b>{owner.name}</b>
              {owner.email ? ` (${owner.email})` : ""}
            </>
          ) : (
            "Der Recruiter der Stelle"
          )}{" "}
          wird per E-Mail informiert.
        </p>
      )}
    </ConfirmDialog>
  );
}
